import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { ActivityTimeline, type ActivityItem } from '@/components/data/ActivityTimeline';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { useLeagueActivity, type TimelineEntry } from '@/features/leagues/league-detail';

const TYPES = [
  'join',
  'leave',
  'draft_pick',
  'trade',
  'waiver',
  'lineup_change',
  'admin_intervention',
] as const;

const searchSchema = z.object({
  type: z.enum(TYPES).optional(),
  page: z.coerce.number().default(1),
});

export const Route = createFileRoute('/leagues/$leagueId/activity')({
  validateSearch: searchSchema,
  component: ActivityTab,
});

const TYPE_LABELS: Record<(typeof TYPES)[number], string> = {
  join: 'Joins',
  leave: 'Leaves',
  draft_pick: 'Draft',
  trade: 'Trades',
  waiver: 'Waivers',
  lineup_change: 'Lineup',
  admin_intervention: 'Admin',
};

function ActivityTab() {
  const { leagueId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, isPending, isError, error, refetch } = useLeagueActivity(
    leagueId,
    search.type,
    search.page,
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChip
          label="All"
          active={!search.type}
          onClick={() => void navigate({ search: { page: 1 } })}
        />
        {TYPES.map((type) => (
          <FilterChip
            key={type}
            label={TYPE_LABELS[type]}
            active={search.type === type}
            onClick={() => void navigate({ search: { type, page: 1 } })}
          />
        ))}
      </div>

      {/*
        Says plainly that this feed is stitched from three logs. An operator who
        assumes it is one collection will look in the wrong place when a row is
        missing — membership changes are not in activityLogsV2 at all.
      */}
      <p className="text-tx3 text-[11.5px]">
        Merged from the league activity log, the append-only membership event log, and this
        console's own audit log. Admin interventions are tinted red so they can be found by
        scanning.
      </p>

      {isError ? (
        <ErrorState error={error} subject="league activity" onRetry={() => void refetch()} />
      ) : isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton height={64} />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </div>
      ) : data.entries.length === 0 ? (
        <EmptyState
          title={search.type ? 'Nothing of that type' : 'No activity yet'}
          description={
            search.type
              ? 'Try clearing the filter — the other logs may still have events.'
              : 'Nothing has happened in this league yet.'
          }
        />
      ) : (
        <>
          {data.capped && (
            <p
              className="rounded-lg border px-3 py-2 text-[12px]"
              style={{
                borderColor: 'rgba(var(--warn-rgb), 0.35)',
                background: 'rgba(var(--warn-rgb), 0.08)',
                color: 'var(--warn)',
              }}
            >
              At least one source hit its read cap, so the oldest events are not shown. This is
              not the beginning of this league's history.
            </p>
          )}

          <ActivityTimeline items={data.entries.map(toActivityItem)} />

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(page) => void navigate({ search: { ...search, page } })}
          />
        </>
      )}
    </div>
  );
}

function toActivityItem(entry: TimelineEntry): ActivityItem {
  const date = entry.at ? new Date(entry.at) : null;

  return {
    id: entry.id,
    type: entry.type,
    occurredAt: entry.at ?? '',
    dayLabel: date
      ? date.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : 'Undated',
    timeLabel: date
      ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : '—',
    summary: (
      <span>
        {entry.message}
        {entry.week !== null && (
          <span className="mesh-mono text-tx3 ml-1.5 text-[11px]">W{entry.week}</span>
        )}
      </span>
    ),
    actorName: entry.actorName ?? undefined,
    detail: (
      <span className="mesh-mono text-tx3 text-[11px]">
        {entry.rawType} · from the {entry.source} log
      </span>
    ),
  };
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="font-head rounded-lg border px-2.5 py-1 text-[11.5px] font-semibold uppercase"
      style={{
        letterSpacing: '0.1em',
        borderColor: active ? 'var(--bd-s)' : 'var(--bd)',
        background: active ? 'var(--high)' : 'transparent',
        color: active ? 'var(--tx)' : 'var(--tx3)',
      }}
    >
      {label}
    </button>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  if (lastPage <= 1) return null;

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="mesh-mono text-tx3 text-[11.5px]">{total.toLocaleString()} events</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Prev
        </button>
        <span className="mesh-mono text-tx3 text-[11.5px]">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
